// 외부 링크 화면 공통 포맷터 (WP5 4단계 §4.4·§5.1)
// 잘못된 입력은 조용히 보정하지 않고 throw 한다(호출 화면이 UNKNOWN 오류로 처리).

const DOW = ['일', '월', '화', '수', '목', '금', '토'] as const;
const KST = 'Asia/Seoul';

function kstParts(now: Date): Record<string, string> {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: KST,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(now);
  const out: Record<string, string> = {};
  for (const p of parts) out[p.type] = p.value;
  return out;
}

/** 서울 시간 기준 오늘 'YYYY-MM-DD' */
export function todayKst(now: Date = new Date()): string {
  const p = kstParts(now);
  return `${p.year}-${p.month}-${p.day}`;
}

/** 서울 시간 기준 지금 'HH:mm' */
export function nowTimeKst(now: Date = new Date()): string {
  const p = kstParts(now);
  return `${p.hour}:${p.minute}`;
}

const DATE_PREFIX = /^\d{4}-\d{2}-\d{2}/;

/** 앞 10자(YYYY-MM-DD). 형식 불일치면 throw */
export function dateOnly(isoLike: string): string {
  if (typeof isoLike !== 'string' || !DATE_PREFIX.test(isoLike)) {
    throw new Error(`dateOnly: 날짜 형식이 아님 (${String(isoLike)})`);
  }
  return isoLike.slice(0, 10);
}

/** 'HH:mm:ss'|'HH:mm' → 'HH:mm'. 형식 불일치면 throw */
export function normalizeTime(t: string): string {
  const m = typeof t === 'string' ? /^(\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/.exec(t) : null;
  if (!m || Number(m[1]) > 23 || Number(m[2]) > 59) {
    throw new Error(`normalizeTime: 시각 형식이 아님 (${String(t)})`);
  }
  return `${m[1]}:${m[2]}`;
}

function parseYmd(ymd: string): Date {
  const m = typeof ymd === 'string' ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd) : null;
  if (!m) throw new Error(`날짜 형식이 아님 (${String(ymd)})`);
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  // 로컬 자정으로 해석(UTC 해석 금지)
  const date = new Date(y, mo - 1, d);
  if (date.getFullYear() !== y || date.getMonth() !== mo - 1 || date.getDate() !== d) {
    throw new Error(`존재하지 않는 날짜 (${ymd})`);
  }
  return date;
}

/** 'YYYY-MM-DD' → 'MM/DD(요일)' */
export function formatMonthDayDow(ymd: string): string {
  const d = parseYmd(ymd);
  return `${ymd.slice(5, 7)}/${ymd.slice(8, 10)}(${DOW[d.getDay()]})`;
}

/** ('YYYY-MM-DD', 'HH:mm[:ss]') → 'MM/DD(요일) HH:mm' */
export function formatMonthDayDowTime(ymd: string, hm: string): string {
  return `${formatMonthDayDow(ymd)} ${normalizeTime(hm)}`;
}

/** 숫자만 추출 */
export function normalizePhone(v: string): string {
  return String(v ?? '').replace(/\D/g, '');
}

/** 숫자만 추출한 값이 ^0\d{9,10}$ */
export function isValidPhone(v: string): boolean {
  return /^0\d{9,10}$/.test(normalizePhone(v));
}

/** 표시용 하이픈 형식. 유효하지 않은 번호는 throw (external-link-send의 formatPhone과 이름 충돌 방지로 formatSharePhone) */
export function formatSharePhone(v: string): string {
  const d = normalizePhone(v);
  if (!isValidPhone(d)) throw new Error('formatSharePhone: 전화번호 형식이 아님');
  if (d.startsWith('02')) {
    return d.length === 9
      ? `${d.slice(0, 2)}-${d.slice(2, 5)}-${d.slice(5)}`
      : `${d.slice(0, 2)}-${d.slice(2, 6)}-${d.slice(6)}`;
  }
  if (d.length === 11) return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
  // 9자리는 02 지역번호만 유효(위 분기). 그 밖은 형식 오류
  throw new Error('formatSharePhone: 전화번호 형식이 아님');
}
